class StringMemory {
  constructor(storage) {
    this.storage = storage;
    this.prefix = "memory";
  }

  async get(key, callback, ...args) {
    if (this.storage.getItem(this.getKeyName(key))) {
      return this.storage.getItem(this.getKeyName(key));
    }

    const result = await callback(...args);

    if (typeof result === "string") {
      this.storage.setItem(this.getKeyName(key), result);
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        "Response of callback must of type string to be stored. Result was not stored"
      );
    }

    return result;
  }

  delete(key) {
    this.storage.removeItem(this.getKeyName(key));
  }

  getKeyName(key) {
    return `${this.prefix}-${key}`;
  }
}

const parseExcludedWarehouseIds = (raw) => {
  if (typeof raw !== "string" || !raw.trim()) {
    return new Set();
  }

  const ids = new Set();

  for (const part of raw.split(",")) {
    const n = Number(part.trim());

    if (!Number.isNaN(n)) {
      ids.add(n);
    }
  }

  return ids;
};

class LocationsInventory extends HTMLElement {
  constructor() {
    super();

    this.data = JSON.parse(this.querySelector("[data-data]").textContent);
    this.productId = this.data.productId;
    this.allProductSKUs = this.data.variants.map((variant) => variant.sku);
    this.excludedWarehouseIds = parseExcludedWarehouseIds(
      this.data.excludedWarehouseIds ?? ""
    );

    this.onlineInventoryElement = this.querySelector(
      '[data-inventory="online"]'
    );
    this.onlineInventoryDot = this.onlineInventoryElement.querySelector(".dot");
    this.onlineInventoryLabel = this.onlineInventoryElement.querySelector(".text__label");
    this.onlineInventoryText =
      this.onlineInventoryElement.querySelector(".text__value");

    this.warehousesInventoryElement = this.querySelector(
      '[data-inventory="warehouses"]'
    );
    this.warehousesInventoryLabel = this.warehousesInventoryElement.querySelector(".text__label");
    this.warehousesInventoryText =
      this.warehousesInventoryElement.querySelector(".text__value");
    this.warehousesInventoryDot =
      this.warehousesInventoryElement.querySelector(".dot");
    this.warehousesInventoryTrigger =
      this.warehousesInventoryElement.querySelector(
        "[data-locations-inventory-trigger]"
      );

    this.fetchLocations = this.fetchLocations.bind(this);

    this.locations = [];

    this.init();
  }

  async init() {
    await this.initLocations();

    document.addEventListener("variant-change", async (event) => {
      this.updateOnlineInventory(event.detail.variant);
      this.updateWarehousesInventory(event.detail.variant.sku);

      if (event.detail.variant) {
        this.classList.remove("locations-inventory--hidden");
      } else {
        this.classList.add("locations-inventory--hidden");
      }
    });
  }

  async fetchLocations() {
    const response = await fetch(
      `https://skechers-api.vercel.app/api/get-stored-inventory?locale=${
        window.locale ?? "dk"
      }`
    );

    if (response.status >= 200 && response.status < 300) {
      return response.text();
    }

    this.warehousesInventoryElement.remove();

    throw new Error(
      `Could not fetch locations. Server responded with: ${response.status}`
    );
  }

  async initLocations() {
    this.warehousesInventoryElement.classList.add("fetching");

    const memory = new StringMemory(sessionStorage);

    try {
      const response = await memory.get(
        `WarehouseWithItems`,
        this.fetchLocations
      );

      const jsonResponse = JSON.parse(response);
      const skuToWarehousesMap = new Map();

      jsonResponse.forEach((warehouse) => {
        const warehouseId = Number(warehouse.warehouseid);

        if (this.excludedWarehouseIds.has(warehouseId)) {
          return;
        }

        const filteredItems = warehouse.parsedItems.filter((item) =>
          this.allProductSKUs.includes(item.sku)
        );

        if (filteredItems.length === 0) return;

        filteredItems.forEach((item) => {
          const { sku, decimaltotal } = item;

          if (!skuToWarehousesMap.has(sku)) {
            skuToWarehousesMap.set(sku, {
              sku,
              warehouses: [],
            });
          }

          const warehouseInfo = {
            warehouseid: warehouse.warehouseid,
            storeid: warehouse.storeid,
            name: warehouse.name,
            city: warehouse.city,
            zip: warehouse.zip,
            address: warehouse.address,
            usetype: warehouse.usetype,
            externalid: warehouse.externalid,
            decimaltotal,
          };

          const relatedSKUs = filteredItems.map((relatedItem) => ({
            sku: relatedItem.sku,
          }));

          skuToWarehousesMap.get(sku).warehouses.push({
            ...warehouseInfo,
            items: relatedSKUs,
          });
        });
      });

      const availability = [...skuToWarehousesMap.values()];

      this.locations = availability;
      this.updateWarehousesInventory(this.data.startVariantSku);
      this.updateOnlineInventory(this.data.startVariant);
    } catch (e) {
      throw e;
    }
  }

  getLocationsWithStock(sku) {
    const locationsWithStock =
      this.locations.find((location) => location.sku === sku)?.warehouses ?? [];
    window.sessionStorage.setItem(
      "locationsWithStock",
      JSON.stringify(locationsWithStock)
    );
    return locationsWithStock;
  }

  updateOnlineInventory(variant) {
    if (!variant) return;
    
    // Find the matching variant from our data to get inventory_quantity
    const variantData = this.data.variants.find(v => v.id === variant.id);
    if (!variantData) return;
    
    const inventory_quantity = variantData.inventory_quantity;
    const available = variant.available;
    const locationsWithStock = this.getLocationsWithStock(variant.sku);

    if (inventory_quantity >= 10) {
      this.onlineInventoryDot.className = "dot dot--green";
      if (locationsWithStock.length === 0) {
        this.onlineInventoryText.textContent = `${this.data.translations.online.in_stock} (${this.data.translations.online.only})`;
      } else {
        this.onlineInventoryText.textContent = this.data.translations.online.in_stock
      }
    } else if (inventory_quantity >= 4) {
      this.onlineInventoryDot.className = "dot dot--green";
      if (locationsWithStock.length === 0) {
        this.onlineInventoryText.textContent = `${this.data.translations.online.in_stock_quantity.replace("%quantity%", inventory_quantity)} (${this.data.translations.online.only})`;
      } else {
        this.onlineInventoryText.textContent = this.data.translations.online.in_stock_quantity.replace("%quantity%", inventory_quantity);
      }
    } else if (inventory_quantity >= 1) {
      this.onlineInventoryDot.className = "dot dot--yellow";
      if (locationsWithStock.length === 0) {
        this.onlineInventoryText.textContent = `${this.data.translations.online.in_stock_limited.replace("%quantity%", inventory_quantity)} (${this.data.translations.online.only})`;
      } else {
        this.onlineInventoryText.textContent = this.data.translations.online.in_stock_limited.replace("%quantity%", inventory_quantity);
      }
    } else {
      this.onlineInventoryDot.className = "dot dot--red";
      if (locationsWithStock.length === 0) {
        this.onlineInventoryText.textContent = `${this.data.translations.online.out_of_stock} (${this.data.translations.online.only})`;
      } else {
        this.onlineInventoryText.textContent = this.data.translations.online.out_of_stock;
      }
    }
  }

  updateWarehousesInventory(sku) {
    const locationsWithStock = this.getLocationsWithStock(sku);

    this.warehousesInventoryElement.classList.remove("fetching");

    if (locationsWithStock.length === 0) {
      this.warehousesInventoryDot.className = "dot dot--red";
      this.warehousesInventoryText.textContent =
        this.data.translations.warehouses.out_of_stock;
      this.warehousesInventoryTrigger.hidden = true;
      this.warehousesInventoryElement.hidden = true;
    } else if (locationsWithStock.length === 1) {
      this.warehousesInventoryDot.className = "dot dot--green";
      this.warehousesInventoryText.textContent =
        this.data.translations.warehouses.in_stock_one;
      this.warehousesInventoryTrigger.hidden = false;
      this.warehousesInventoryElement.hidden = false;
    } else {
      this.warehousesInventoryDot.className = "dot dot--green";
      this.warehousesInventoryText.textContent =
        this.data.translations.warehouses.in_stock.replace(
          "{{ count }}",
          locationsWithStock.length
        );
      this.warehousesInventoryTrigger.hidden = false;
      this.warehousesInventoryElement.hidden = false;
    }
  }
}

customElements.define("locations-inventory", LocationsInventory);
